/**
 * Setup Wizard API service
 * For users in pending_setup status to complete their account setup
 */

import axiosInstance from './axiosInstance';
import type { PanLumaRequestConfig } from './axiosInstance';
// The setup-wizard routes carry response/request models, so their payload types
// are generated — imported and re-exported here rather than re-typed, so
// consumers keep importing them from this module and the shapes cannot drift.
import type { CreateTenantRequest } from '@/generated/api/models/createTenantRequest';
import type { JoinTenantRequest } from '@/generated/api/models/joinTenantRequest';
import type { ConfirmPersonRequest } from '@/generated/api/models/confirmPersonRequest';
import type { ModulesAdminApiDemoProvisioningRoutesDemoVariationResponse } from '@/generated/api/models/modulesAdminApiDemoProvisioningRoutesDemoVariationResponse';

export interface SetupModule {
  id: string;
  key: string;
  name_key: string;
  description_key: string;
  icon: string;
  /**
   * Sharing Infra P-4: when set, this module exposes a tenant-wide default
   * visibility mode (the object_type it keys, e.g. "pipeline.deal"). The wizard
   * shows a per-module visibility question and seeds `entity_visibility_modes`.
   */
  visibility_object_type?: string | null;
}

/** The wizard answers the module-recommendation rules are evaluated against. */
export interface ModuleRecommendationSignals {
  jobFunction?: string | null;
  jobLevel?: string | null;
  /** `business_classification.config.json` `business_models[].value` entries. */
  businessModels?: string[];
  industry?: string | null;
  /** 'consumer' | 'smb' | 'enterprise'. */
  customerTypes?: string[];
  /** `company_size_band.config.json` key, e.g. 'small'. */
  companySizeBand?: string | null;
}

export interface ModuleRecommendations {
  /** Pre-checked on the Modules step. */
  recommended_modules: string[];
  /** Alpha modules the rules matched — marked in the experimental section, never checked. */
  suggested_modules: string[];
}

/**
 * A demo-data variation offered in the onboarding picker.
 *
 * This IS the demo-PROVISIONING variation model (it carries `available`), which
 * is a different model from the public demo picker's `DemoVariation` in
 * `shared/auth/demoApi.ts` — that route deliberately exposes no availability
 * flag, since a publicly advertised variation is available by definition. Two
 * routes, two response models, so each aliases its own.
 */
export type SetupDemoVariation = ModulesAdminApiDemoProvisioningRoutesDemoVariationResponse;

/** The onboarding variation catalog + the industry-recommended key (§4.4). */
export interface SetupDemoCatalog {
  variations: SetupDemoVariation[];
  recommended_variation: string;
}

export interface InvitationOption {
  type: 'invitation';
  tenant_id: string;
  tenant_name: string;
  /** Null when the inviter had no primary company; the join resolves the tenant default. */
  company_id: string | null;
  company_name: string | null;
  invited_by_name: string | null;
  invited_by_email: string | null;
  is_admin: boolean;
}

export interface DomainMatchOption {
  type: 'domain_match';
  tenant_id: string;
  tenant_name: string;
  companies: Array<{ id: string; name: string }>;
}

export type SetupOption = InvitationOption | DomainMatchOption;

/**
 * Domain matched an active tenant whose self-registration is OFF: the user can
 * request to join (tenant-admin approval) rather than being forced to create a
 * duplicate new tenant. Shape mirrors DomainMatchOption.
 */
export interface DomainMatchRequestOption {
  tenant_id: string;
  tenant_name: string;
  companies: Array<{ id: string; name: string }>;
}

export interface SetupOptionsResponse {
  has_invitation: boolean;
  has_domain_match: boolean;
  invitation: InvitationOption | null;
  domain_match: DomainMatchOption | null;
  has_domain_match_request: boolean;
  domain_match_request: DomainMatchRequestOption | null;
}

/** A pending request to join a tenant (returned by GET /setup-wizard/join-request). */
export interface MyJoinRequest {
  id: string;
  tenant_id: string;
  tenant_name: string | null;
  company_id: string | null;
  status: string;
  created_at: string | null;
}

// Request body for POST /setup-wizard/join-tenant. `company_id` is OPTIONAL on
// the generated model (an invitation that names no company resolves the tenant
// default server-side), and `is_admin` is DEPRECATED/IGNORED — admin standing is
// derived from the invitation, never from the client value.
export type { JoinTenantRequest };

// Request body for POST /setup-wizard/create-tenant. Three fields are narrower
// than the shape this module used to hand-roll, and all three are closed enums
// the backend validates: `company_size_band` (company_size_band.config.json),
// `signup_referral_source` (signup_referral_source.config.json) and each value
// of `visibility_modes` (VisibilityMode). Sharing Infra P-4: `visibility_modes`
// carries the per-object_type default chosen in the wizard (e.g.
// { "pipeline.deal": "owner_and_shared" }); the backend writes no row for
// `everyone` (absent row == everyone).
export type { CreateTenantRequest };

// --- v2 prefill / person contracts (Onboarding #5 + #6) ---

export type PrefillConfidence = 'high' | 'medium' | 'low';
export type PrefillStatus = 'pending' | 'ready' | 'failed';

export interface PrefillField<T> {
  value: T | null;
  confidence: PrefillConfidence;
}

export interface CompanyPrefill {
  name: PrefillField<string>;
  website: PrefillField<string>;
  industry: PrefillField<string>;
  customer_types: PrefillField<string[]>;
  size_band: PrefillField<string>;
  primary_country: PrefillField<string>;
  description: PrefillField<string>;
  logo_file_id: string | null;
  brand_color: string | null;
}

export interface PrefillPersonCandidate {
  linkedin_url: string;
  headline_snippet: string;
  photo_thumb_url: string | null;
  raw_score: number;
  normalized_confidence: PrefillConfidence;
}

export interface PersonPrefill {
  candidates: PrefillPersonCandidate[];
  best_candidate_id: string | null;
}

export interface PrefillDefaults {
  country_code: string | null;
  timezone: string | null;
  language: string | null;
}

export interface SetupWizardPrefill {
  status: PrefillStatus;
  /**
   * True when the user registered with a free/consumer email domain
   * (gmail/yahoo/…). The domain enrichment lane is skipped for these, so the
   * wizard opens Step 1 in "ask + fetch + confirm" lookup mode (Onboarding #8)
   * instead of a blank Confirm-Company form.
   */
  generic_domain: boolean;
  company: CompanyPrefill;
  person: PersonPrefill;
  defaults: PrefillDefaults;
}

// Request body for POST /setup-wizard/confirm-person: the LinkedIn URL the user
// confirmed (or pasted), plus the original search snippet used as the AI
// extraction input when the LinkedIn page itself can't be fetched.
export type { ConfirmPersonRequest };

export interface PersonProfile {
  linkedin_url: string;
  full_name: string | null;
  headline: string | null;
  role: string | null;
  photo_url: string | null;
  photo_file_id: string | null;
  snippet_only: boolean;
  errors: string[];
}

export interface FallbackResolveResponse {
  domain: string;
  company: CompanyPrefill;
  defaults: PrefillDefaults;
}

export interface SubdomainSuggestion {
  can_claim: boolean;
  suggested_subdomain: string | null;
  full_domain: string | null;
  base_domain: string | null;
  email_domain: string | null;
}

/** Machine-readable outcome of a live subdomain availability check. */
export type SubdomainAvailabilityReason =
  | 'available'
  | 'invalid_format'
  | 'too_short'
  | 'too_long'
  | 'reserved'
  | 'taken';

export interface SubdomainAvailability {
  available: boolean;
  reason: SubdomainAvailabilityReason;
  full_domain: string | null;
  base_domain: string;
}

export interface SetupCompleteResponse {
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    status: string;
    is_admin: boolean;
    tenant_id: string;
    company_id: string;
  };
  tenant?: {
    id: string;
    name: string;
  };
  company?: {
    id: string;
    name: string;
  };
  custom_domain?: {
    id: string;
    domain: string;
    status: string;
  };
  /**
   * Auth tokens are issued only when the tenant goes live immediately. When the
   * sysadmin tenant-approval gate is on the tenant + user are held in
   * `pending_approval` and NO tokens are returned — `pending_approval` is true
   * instead (the wizard then shows the "application received" screen).
   */
  access_token?: string;
  refresh_token?: string;
  /** True when the gate held the tenant for sysadmin review (no tokens issued). */
  pending_approval?: boolean;
  /** Message code for the pending-approval result (`TENANT_PENDING_APPROVAL`). */
  message_code?: string;
}

/**
 * Envelope-unwrapping request. The setup-wizard routes return the standard
 * `{ data: ... }` envelope.
 *
 * Every call goes through the shared `axiosInstance` so the wizard inherits the
 * platform's ONE error-handling contract rather than carrying its own: the
 * default request timeout (a stalled backend surfaces as `errors:TIMEOUT` via
 * `getErrorMessage`, instead of a promise that never settles and a spinner that
 * never stops), the auth/refresh interceptors, and the `{detail:{code,message}}`
 * envelope that `getErrorMessage`/`getFieldErrors` already translate.
 *
 * The wizard used to hand-roll `fetch` here with no deadline and its own
 * `SetupWizardApiError`, which is why a backend that restarted mid-request left
 * /setup spinning forever with no error and no way out.
 */
async function request<T>(config: PanLumaRequestConfig): Promise<T> {
  const response = await axiosInstance.request<{ data: T }>(config);
  return response.data.data;
}

/**
 * Like {@link request} but for endpoints that return the model directly (no
 * `{ data: ... }` envelope) — e.g. the demo-data routes.
 */
async function requestRaw<T>(config: PanLumaRequestConfig): Promise<T> {
  const response = await axiosInstance.request<T>(config);
  return response.data;
}

const SETUP = '/api/v1/setup-wizard';

/**
 * Demo-data provisioning seeds a whole tenant, so it is one of the few calls
 * that genuinely outlives the shared default deadline. It is also best-effort —
 * the tenant already exists by the time it runs — so timing out degrades to a
 * soft notice rather than failing onboarding.
 */
const DEMO_PROVISION_TIMEOUT_MS = 300_000;

export const setupWizardApi = {
  /** Get available setup options for the current pending user */
  getOptions(): Promise<SetupOptionsResponse> {
    return request<SetupOptionsResponse>({ method: 'GET', url: `${SETUP}/options` });
  },

  /** Get detected country code based on client IP (GeoIP) */
  getGeoipCountry(): Promise<{ country_code: string | null }> {
    return request<{ country_code: string | null }>({
      method: 'GET',
      url: `${SETUP}/geoip-country`,
    });
  },

  /**
   * Whether a self-serve-created tenant will be held for sysadmin approval.
   * Drives the create-tenant step's application-vs-instant-create copy.
   */
  getTenantApprovalRequired(): Promise<{ require_tenant_approval: boolean }> {
    return request<{ require_tenant_approval: boolean }>({
      method: 'GET',
      url: `${SETUP}/tenant-approval-required`,
    });
  },

  /**
   * Unauthenticated geo-IP locale defaults for pre-auth screens (register/login).
   * Returns the detected country plus the UI language (restricted to the app's
   * supported set) and ISO currency derived from it — so the registration page
   * can default to Hebrew/ILS in Israel before the visitor has logged in.
   */
  getPublicLocaleDefaults(): Promise<{
    country_code: string | null;
    language: string;
    currency: string;
  }> {
    return request<{ country_code: string | null; language: string; currency: string }>({
      method: 'GET',
      url: `${SETUP}/public/locale-defaults`,
      skipAuth: true,
    });
  },

  /**
   * Unauthenticated read of the same gate, for pre-auth screens that have no
   * setup token yet (the first signup screen, the email-verification result).
   * Returns only the global boolean — no tenant data.
   */
  getPublicTenantApprovalRequired(): Promise<{ require_tenant_approval: boolean }> {
    return request<{ require_tenant_approval: boolean }>({
      method: 'GET',
      url: `${SETUP}/public/tenant-approval-required`,
      skipAuth: true,
    });
  },

  /**
   * Get available modules for selection.
   * If tenant_id provided, returns only modules enabled for that tenant.
   */
  getModules(tenantId?: string): Promise<{ modules: SetupModule[] }> {
    return request<{ modules: SetupModule[] }>({
      method: 'GET',
      url: `${SETUP}/modules`,
      params: tenantId ? { tenant_id: tenantId } : undefined,
    });
  },

  /** Complete setup by joining an existing tenant */
  joinTenant(data: JoinTenantRequest): Promise<SetupCompleteResponse> {
    return request<SetupCompleteResponse>({
      method: 'POST',
      url: `${SETUP}/join-tenant`,
      data,
    });
  },

  /** Complete setup by creating a new tenant */
  createTenant(data: CreateTenantRequest): Promise<SetupCompleteResponse> {
    return request<SetupCompleteResponse>({
      method: 'POST',
      url: `${SETUP}/create-tenant`,
      data,
    });
  },

  /**
   * Onboarding demo-data catalog: the variations + the industry-recommended key
   * to pre-select in the wizard's "Load sample data?" step (§4.4). Served to
   * pending_setup users (no tenant yet).
   */
  getDemoCatalog(industry?: string | null): Promise<SetupDemoCatalog> {
    return requestRaw<SetupDemoCatalog>({
      method: 'GET',
      url: '/api/v1/demo-data/onboarding-catalog',
      params: industry ? { industry } : undefined,
    });
  },

  /**
   * Provision the caller's freshly-created tenant with a demo variation. Called
   * AFTER create-tenant succeeds and the new active token is in storage (the
   * tenant must exist and the caller be its admin). Returns nothing useful to the
   * wizard, so the response is ignored.
   *
   * Seeding a whole demo tenant legitimately outruns the shared default
   * deadline, so this is one of the rare calls that opts into a longer one
   * rather than being capped by it.
   */
  async provisionDemo(variation: string): Promise<void> {
    await requestRaw<unknown>({
      method: 'POST',
      url: '/api/v1/demo-data/provision',
      data: { variation },
      timeout: DEMO_PROVISION_TIMEOUT_MS,
    });
  },

  /**
   * Request to join a domain-matched tenant whose self-registration is OFF.
   * Creates a pending request a tenant admin must approve.
   */
  createJoinRequest(data: {
    tenant_id: string;
    company_id?: string | null;
  }): Promise<MyJoinRequest & { tenant_name: string }> {
    return request<MyJoinRequest & { tenant_name: string }>({
      method: 'POST',
      url: `${SETUP}/join-request`,
      data,
    });
  },

  /** Get the current pending user's outstanding join request, or null. */
  getMyJoinRequest(): Promise<MyJoinRequest | null> {
    return request<MyJoinRequest | null>({ method: 'GET', url: `${SETUP}/join-request` });
  },

  /** Cancel the current pending user's join request (to create a tenant instead). */
  cancelJoinRequest(): Promise<{ cancelled: boolean }> {
    return request<{ cancelled: boolean }>({ method: 'DELETE', url: `${SETUP}/join-request` });
  },

  /** Decline a pending invitation */
  declineInvitation(): Promise<{ message_code: string }> {
    return request<{ message_code: string }>({
      method: 'POST',
      url: `${SETUP}/decline-invitation`,
    });
  },

  /**
   * Get the modules the wizard should pre-select for this company + role.
   *
   * The single source of module recommendations for the wizard: every module
   * owns its conditions in `role_module_recommendations.config.json`, evaluated
   * against the company's answers and the signer-up's role.
   * `recommended_modules` are pre-checked; `suggested_modules` are the alpha
   * modules the same rules matched, which the step marks but never checks.
   */
  getRecommendedModules(
    signals: ModuleRecommendationSignals
  ): Promise<ModuleRecommendations> {
    const { jobFunction, jobLevel, businessModels, industry, customerTypes, companySizeBand } =
      signals;
    return request<ModuleRecommendations>({
      method: 'GET',
      url: `${SETUP}/recommended-modules`,
      params: {
        ...(jobFunction ? { job_function: jobFunction } : {}),
        ...(jobLevel ? { job_level: jobLevel } : {}),
        ...(businessModels?.length ? { business_model: businessModels } : {}),
        ...(industry ? { industry } : {}),
        ...(customerTypes?.length ? { customer_type: customerTypes } : {}),
        ...(companySizeBand ? { company_size_band: companySizeBand } : {}),
      },
    });
  },

  /** Get subdomain suggestion based on user's email domain */
  getSubdomainSuggestion(): Promise<SubdomainSuggestion> {
    return request<SubdomainSuggestion>({
      method: 'GET',
      url: `${SETUP}/subdomain-suggestion`,
    });
  },

  /**
   * Check whether a user-typed subdomain can be claimed (live, debounced).
   * Distinct from getSubdomainSuggestion, which only evaluates the
   * email-derived name. Read-only — never reserves the subdomain.
   */
  checkSubdomainAvailability(subdomain: string): Promise<SubdomainAvailability> {
    return request<SubdomainAvailability>({
      method: 'GET',
      url: `${SETUP}/subdomain-availability`,
      params: { subdomain },
    });
  },

  /**
   * Get AI prefill for the v2 wizard (company + person + recommended modules).
   * Backend short-polls its cache; status may still be 'pending' on return.
   */
  getPrefill(): Promise<SetupWizardPrefill> {
    return request<SetupWizardPrefill>({ method: 'GET', url: `${SETUP}/prefill` });
  },

  /** Confirm a LinkedIn candidate (or pasted URL) and extract the full person profile. */
  confirmPerson(data: ConfirmPersonRequest): Promise<PersonProfile> {
    return request<PersonProfile>({ method: 'POST', url: `${SETUP}/confirm-person`, data });
  },

  /** Resolve a company name or website to a fresh company prefill (generic-domain fallback). */
  fallbackResolve(query: string): Promise<FallbackResolveResponse> {
    return request<FallbackResolveResponse>({
      method: 'POST',
      url: `${SETUP}/fallback-resolve`,
      data: { query },
    });
  },
};
